import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { useCompleteObservation, useObservationDetail } from '@/api/practicalObservations';
import {
  CriteriaChecklist,
  type ChecklistEntry,
} from '@/components/observation/criteria-checklist';
import { EvidenceDropzone } from '@/components/observation/evidence-dropzone';
import { InstructorMaterialViewer } from '@/components/observation/instructor-material-viewer';
import type { EvidenceDraft } from '@/types';

export function ObservationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const navigate = useNavigate();

  const { data: response, isLoading, error } = useObservationDetail(requestId);
  const complete = useCompleteObservation();

  const [results, setResults] = useState<ChecklistEntry[]>([]);
  const [evidence, setEvidence] = useState<EvidenceDraft[]>([]);
  const [notes, setNotes] = useState('');

  const request = response?.data;

  const requiredCount = useMemo(() => {
    if (!request) return 0;
    return request.lesson.observation_criteria.filter((c) => c.required).length;
  }, [request]);

  const submittedRequired = useMemo(() => {
    if (!request) return 0;
    return results.filter(
      (r) =>
        r.passed !== null &&
        request.lesson.observation_criteria.find((c) => c.id === r.criterion_id && c.required),
    ).length;
  }, [results, request]);

  if (isLoading || !request) {
    return <div className="p-6 text-sm text-muted-foreground">Cargando solicitud…</div>;
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-rose-600">
        Error: {error instanceof Error ? error.message : 'Desconocido'}
      </div>
    );
  }

  if (request.status !== 'pending') {
    return (
      <div className="p-6">
        <button
          type="button"
          onClick={() => navigate('/instructor/observations')}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Volver
        </button>
        <p className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-sm">
          Esta solicitud ya está {request.status === 'completed' ? 'completada' : 'cancelada'}.
        </p>
      </div>
    );
  }

  const canSubmit = submittedRequired >= requiredCount;

  const allCriteriaIds = new Set(request.lesson.observation_criteria.map((c) => c.id));

  function buildPayload() {
    return results
      .filter((r) => r.passed !== null && allCriteriaIds.has(r.criterion_id))
      .map((r) => ({
        criterion_id: r.criterion_id,
        passed: r.passed as boolean,
        notes: r.notes || undefined,
      }));
  }

  async function handleSubmit() {
    try {
      await complete.mutateAsync({
        id: requestId,
        evaluator_notes: notes.trim() || undefined,
        criteria_results: buildPayload(),
        evidence_keys: evidence,
      });
      navigate('/instructor/observations');
    } catch {
      // shown below via complete.error
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-6">
      <button
        type="button"
        onClick={() => navigate('/instructor/observations')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Volver a pendientes
      </button>

      <h1 className="text-2xl font-bold text-foreground">Confirmar observación práctica</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">
          {request.enrollment.user.first_name} {request.enrollment.user.last_name}
        </span>
        {' · '}
        {request.enrollment.course.title} · {request.lesson.title}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        Solicitada el {new Date(request.requested_at).toLocaleString()}
      </p>

      <div className="mt-6 space-y-6">
        <InstructorMaterialViewer
          material={request.lesson.instructor_material}
          fileKey={request.lesson.instructor_material_file_key}
        />

        <section>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            Criterios{' '}
            <span className="text-xs font-normal text-muted-foreground">
              ({submittedRequired}/{requiredCount} requeridos completados)
            </span>
          </h2>
          <CriteriaChecklist
            criteria={request.lesson.observation_criteria}
            value={results}
            onChange={setResults}
          />
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            Evidencia <span className="text-xs font-normal text-muted-foreground">(opcional)</span>
          </h2>
          <EvidenceDropzone requestId={requestId} value={evidence} onChange={setEvidence} />
        </section>

        <section>
          <h2 className="mb-2 text-sm font-semibold text-foreground">
            Nota global{' '}
            <span className="text-xs font-normal text-muted-foreground">(opcional)</span>
          </h2>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Observación general, recomendación de mejora, contexto…"
            className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
          />
        </section>

        {complete.error && (
          <p className="text-sm text-rose-600">
            {complete.error instanceof Error
              ? complete.error.message
              : 'No se pudo completar la observación.'}
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => navigate('/instructor/observations')}
            className="rounded border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!canSubmit || complete.isPending}
            onClick={handleSubmit}
            className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {complete.isPending ? 'Guardando…' : 'Confirmar observación'}
          </button>
        </div>
        {!canSubmit && (
          <p className="text-right text-xs text-rose-600">
            Faltan {requiredCount - submittedRequired} criterios requeridos.
          </p>
        )}
      </div>
    </div>
  );
}
